import { useState } from 'react';

export default function BookWaitingList() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !name) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('name', name);
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4euj4ca42c0argjm8kg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setName('');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="book" className="py-20 bg-gradient-to-br from-teal-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          
          {/* Book Cover & Info */}
          <div>
            <div className="bg-white rounded-2xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-300">
              <img
                src="https://public.readdy.ai/ai/img_res/2a4d5e2e4a62516dd9e22dc9b7d27bcd.jpg"
                alt="AI Is More Human Than Humans book cover"
                title="AI Is More Human Than Humans - Exploring the Consistency Paradox"
                className="w-full rounded-lg shadow-lg mb-6"
              />
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  <strong>AI Is More Human Than Humans</strong>
                </h3>
                <p className="text-lg text-gray-600 mb-4">
                  Exploring the <strong>Consistency Paradox</strong>
                </p>
                <p className="text-sm text-teal-600 font-semibold">
                  Expected Publication: Early 2026
                </p>
              </div>
            </div>
          </div>

          {/* Waiting List Form */}
          <div>
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-4">
                Coming Early 2026
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Join the <strong>Waiting List</strong>
              </h2>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                Be the first to know when <strong>"AI Is More Human Than Humans"</strong> is available. 
                Get exclusive updates, early access, and special launch pricing.
              </p>
            </div>

            {/* Benefits */}
            <div className="mb-8 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-notification-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Early Access Notification</h4>
                  <p className="text-gray-600 text-sm">Be among the first to get the book when it launches</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Special Launch Pricing</h4>
                  <p className="text-gray-600 text-sm">Exclusive discounts for waiting list members</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-book-open-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Bonus Content</h4>
                  <p className="text-gray-600 text-sm">Receive exclusive chapters and insights before publication</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form 
              id="book-waiting-list-form"
              onSubmit={handleSubmit} 
              className="space-y-4"
              data-readdy-form
            >
              <div>
                <label htmlFor="waiting-list-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="waiting-list-name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label htmlFor="waiting-list-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="waiting-list-email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                  placeholder="your.email@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Joining...' : 'Join the Waiting List'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Success!</strong> You're on the waiting list. Check your email for confirmation.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Error:</strong> Something went wrong. Please try again.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}
